import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CheckBox,
  Icon,
  Input,
  Message,
  Modal,
  Select,
} from '@iwoca/orion';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { TDeserialisedIntroducerFormData } from 'types';
import styles from './RegistrationForm.module.css';
import { useRegisterIntroducerMutation } from './registrationHelper';

type RegistrationFormDataDE = {
  // Company information
  introducerType: 'broker' | 'accountant';
  companyName: string;
  vatNumber?: string;
  companyNumber?: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  postcode: string;
  companyWebsite?: string;
  // Personal information
  title: 'Herr' | 'Frau';
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  privacyPolicyAgreement: boolean;
};

const personalInformationSchema = yup.object().shape({
  title: yup.string().required('Dies ist ein Pflichtfeld'),
  firstName: yup.string().required('Vorname ist erforderlich'),
  lastName: yup.string().required('Nachname ist erforderlich'),
  email: yup
    .string()
    .email('Bitte verwenden Sie ein gültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phoneNumber: yup.string().required('Telefonnummer ist erforderlich'),
  privacyPolicyAgreement: yup
    .boolean()
    .required('Datenschutzerklärung ist erforderlich')
    .oneOf([true], 'Die AGBs müssen akzeptiert werden'),
});

// Need to translate the errors properly
const fullRegistrationSchema = personalInformationSchema.shape({
  companyName: yup.string().required('Firmenname ist erforderlich'),
  vatNumber: yup.string().min(0).max(20),
  companyNumber: yup.string(),
  addressLine1: yup.string().required('Adresszeile 1 ist erforderlich'),
  addressLine2: yup.string().required('Adresszeile 2 ist erforderlich'),
  town: yup.string().required('Stadt ist erforderlich'),
  postcode: yup.string().required('Postleitzahl der Firma ist erforderlich'),
  companyWebsite: yup.string(),
});

export const RegistrationFormDE = () => {
  const router = useRouter();
  const inviteId = router.query.introducer as string | undefined;
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegistrationFormDataDE>({
    defaultValues: {
      introducerType: 'broker',
    },
    resolver: yupResolver(
      inviteId ? personalInformationSchema : fullRegistrationSchema,
    ),
  });

  const signUpMutation = useRegisterIntroducerMutation({
    country: 'Germany',
    onError: () => setShowErrorModal(true),
  });

  const onSubmit = (
    values: Omit<TDeserialisedIntroducerFormData, 'registeredTradingAddress'>,
  ) => {
    if (!isConfirming) {
      return setIsConfirming(true);
    }
    signUpMutation.mutate(values);
  };

  const renderCompanyInfo = () => {
    return (
      <>
        <h2>Informationen über das Unternehmen</h2>
        <Card className={styles.companyInfoContainer}>
          <div className={styles.formField}>
            <Input
              {...register('companyName')}
              id="companyName"
              name="companyName"
              type="text"
              label="Firmenname"
              placeholder="Iwoca"
              disabled={isConfirming}
              error={errors.companyName && errors.companyName.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('vatNumber')}
              id="vatNumber"
              name="vatNumber"
              type="text"
              label="Ust. Nummer (optional)"
              placeholder="123456"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyNumber')}
              id="companyNumber"
              name="companyNumber"
              type="text"
              label="Handelsregisternummer (HR) (optional)"
              placeholder="12345678"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyWebsite')}
              id="companyWebsite"
              name="companyWebsite"
              type="text"
              label="Webseite (optional)"
              placeholder="www.iwoca.de"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine1')}
              id="addressLine1"
              name="addressLine1"
              type="text"
              label="Straße"
              placeholder="Frankfurter Straße"
              disabled={isConfirming}
              error={errors.addressLine1 && errors.addressLine1.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine2')}
              id="addressLine2"
              name="addressLine2"
              type="text"
              label="Hausnummer"
              placeholder="12"
              disabled={isConfirming}
              error={errors.addressLine2 && errors.addressLine2.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('postcode', {
                setValueAs: (text: string) => text.toUpperCase(),
              })}
              id="postcode"
              name="postcode"
              type="text"
              label="Postleitzahl"
              placeholder="12345"
              disabled={isConfirming}
              error={errors.postcode && errors.postcode.message}
              className={styles.uppercaseText}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('town')}
              id="town"
              name="town"
              type="text"
              label="Stadt"
              placeholder="Frankfurt"
              disabled={isConfirming}
              error={errors.town && errors.town.message}
            />
          </div>
        </Card>
      </>
    );
  };

  const renderAccountInfo = () => {
    return (
      <>
        <h2>Persönliche Informationen</h2>
        <Card className={styles.accountInformationContainer}>
          <div className={styles.formField}>
            <Select
              {...register('title')}
              label="Anrede"
              id="title"
              name="title"
              type="text"
              disabled={isConfirming}
            >
              <option value="Herr">Herr</option>
              <option value="Frau">Frau</option>
            </Select>
          </div>
          <div className={styles.formField}>
            <Input
              {...register('firstName')}
              id="firstName"
              name="firstName"
              type="text"
              label="Vorname"
              placeholder="Max"
              disabled={isConfirming}
              error={errors.firstName && errors.firstName.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('lastName')}
              id="lastName"
              name="lastName"
              type="text"
              label="Nachname"
              placeholder="Mustermann"
              disabled={isConfirming}
              error={errors.lastName && errors.lastName.message}
            />
          </div>
          <div className={clsx(styles.formField, styles.email)}>
            <Input
              {...register('email')}
              id="email"
              name="email"
              type="text"
              label="E-Mail"
              placeholder="ich@iwoca.de"
              disabled={isConfirming}
              error={errors.email && errors.email.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('phoneNumber')}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              label="Telefonnummer"
              placeholder="+49 123456789"
              disabled={isConfirming}
              error={errors.phoneNumber && errors.phoneNumber.message}
            />
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <h1>Erstellen Sie ein Partnerprofil</h1>
      {!inviteId && (
        <p className={styles.inviteInformation}>
          Ist Ihr Unternehmen bereits ein Partner von uns? Lassen Sie sich von
          Ihren Kolleg*innen einen Einladungslink geben, um sofort loszulegen.
        </p>
      )}
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {!inviteId && renderCompanyInfo()}
        {renderAccountInfo()}
        <div className={styles.footerItem}>
          <div className={styles.privacyPolicyAgreement}>
            <CheckBox
              {...register('privacyPolicyAgreement')}
              id="privacyPolicyAgreement"
              name="privacyPolicyAgreement"
              data-testid="privacyPolicyAgreement"
              disabled={isConfirming}
            />
            <div className={styles.privacyPolicyDescription}>
              <p>
                Ich habe die{' '}
                <a
                  href="https://assets.ctfassets.net/835lsoe3tdrh/7wD453HBeF5ZnmFrGkUUJo/62728e5e799d48a3ecef4da27475d7a9/iwocaDeutschland_Partnerprogramms_TandCs.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  iwoca Partner AGB
                </a>{' '}
                gelesen und stimme diesen zu.
              </p>
              {errors.privacyPolicyAgreement && (
                <Message
                  message={errors.privacyPolicyAgreement.message || ''}
                  type="error"
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant={isConfirming ? 'success' : 'primary'}
            type="submit"
            loading={signUpMutation.isPending || signUpMutation.isSuccess}
          >
            {isConfirming ? 'Bestätigen' : 'Anmelden'}
            <Icon icon="lockCloseFilled" />
          </Button>
          {isConfirming && (
            <Button
              className={styles.cancelButton}
              variant="tertiary"
              onClick={() => setIsConfirming(false)}
            >
              Abbrechen
              <Icon icon="timesDefault" />
            </Button>
          )}
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            Auf eine erfolgreiche Partnerschaft!
          </p>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Es gibt ein Problem mit den Daten, die Sie eingegeben haben."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          Wenn Sie ein Konto bei iwoca haben, melden Sie sich über den
          Login-Button oben rechts an. Wenn nicht, dann rufen Sie uns gerne an
          und wir helfen Ihnen bei der Anmeldung.
        </p>
      </Modal>
    </div>
  );
};
